import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatMoney, hoy } from '@abastece/shared';
import { TRAMOS_FIADO, type TramoFiado } from '../lib/analisis.js';
import { useMonedaDeLosLibros } from '../lib/libros.js';
import { leerFiado, ordenarDeudores, pasoSuLimite, type OrdenFiado } from '../lib/porCobrar.js';
import { usePuede } from '../lib/sesion.js';
import { useT, type Clave } from '../i18n/index.js';
import { Campo } from '../ui/Campo.js';
import { fechaCorta } from '../ui/CuentasDeCaja.js';
import { FilaMoneda } from '../ui/ElegirMoneda.js';
import { Barras } from '../ui/Graficas.js';
import { Pagina, Vacio } from '../ui/Pagina.js';

/** Each age of a debt, with the colour «Análisis» gives it. */
const TRAMO: Record<TramoFiado, { nombre: Clave; tono: string }> = {
  '30': { nombre: 'analisis.tramo30', tono: 'bg-emerald-500' },
  '60': { nombre: 'analisis.tramo60', tono: 'bg-amber-400' },
  '90': { nombre: 'analisis.tramo90', tono: 'bg-orange-500' },
  mas: { nombre: 'analisis.tramoMas', tono: 'bg-rose-500' },
};

const ORDENES: { valor: OrdenFiado; nombre: Clave }[] = [
  { valor: 'antiguo', nombre: 'porCobrar.ordenAntiguo' },
  { valor: 'monto', nombre: 'porCobrar.ordenMonto' },
];

/**
 * «Fiado»: what the clients owe, all of it (the owner, 2026-10-07: «a "Credit" button that shows all
 * outstanding receivables»). The total and how old it is, then everybody who owes, the oldest debt
 * first, each with their limit and their last payment. A row opens the client's account, where the
 * abono is taken and the statement sent. «Análisis» shows the ten owing longest; this is the whole
 * notebook, a client taken off the list included, since what they owe is still owed.
 *
 * Córdobas or dollars for the owner (2026-10-14: «credit page should have a cordobas/dollar view
 * toggle»), the books' «C$ | US$» and the phone's one choice of it (`useMonedaDeLosLibros`). A cashier,
 * who takes the abonos in córdobas, reads córdobas and has no switch, as on every screen of hers.
 */
export function Fiado() {
  const t = useT();
  const hoyEs = hoy();
  const libros = useMonedaDeLosLibros();
  const enDolaresSePuede = usePuede('ver_reportes');
  const dinero = enDolaresSePuede ? libros.dinero : formatMoney;
  const datos = useLiveQuery(() => leerFiado(hoyEs), [hoyEs]);
  const [consulta, setConsulta] = useState('');
  const [orden, setOrden] = useState<OrdenFiado>('antiguo');

  const fiado = datos?.fiado;
  const visibles = datos ? ordenarDeudores(datos.deudores, orden, consulta) : [];

  return (
    <Pagina
      titulo={t('porCobrar.titulo')}
      subtitulo={fiado && fiado.total > 0 ? t('analisis.fiadoTotal', { monto: dinero(fiado.total), n: fiado.clientes.length }) : undefined}
    >
      {!datos || !fiado ? null : fiado.total <= 0 ? (
        <Vacio mensaje={t('analisis.sinFiado')} icono="fiado" />
      ) : (
        <div className="flex flex-col gap-4">
          {enDolaresSePuede && <FilaMoneda moneda={libros.moneda} onElegir={libros.elegir} tasa={libros.tasa} className="" />}
          <section className="tarjeta" data-fiado-antiguedad>
            <h2 className="font-semibold">{t('analisis.fiado')}</h2>
            <p className="mt-0.5 mb-4 text-xs text-slate-500">{t('analisis.fiadoAyuda')}</p>
            <Barras
              filas={TRAMOS_FIADO.map((tramo) => {
                const x = fiado.tramos.find((y) => y.tramo === tramo)!;
                return {
                  clave: tramo,
                  etiqueta: t(TRAMO[tramo].nombre),
                  valor: x.monto,
                  texto: dinero(x.monto),
                  detalle: t('analisis.nClientes', { n: x.clientes }),
                  tono: TRAMO[tramo].tono,
                };
              })}
              datos="tramos"
            />
          </section>

          <div className="flex flex-col gap-3">
            <Campo
              etiqueta={t('comun.buscar')}
              value={consulta}
              onChange={(e) => setConsulta(e.target.value)}
              placeholder={t('pos.nombreOTelefono')}
              autoComplete="off"
            />
            <div className="flex gap-1 rounded-xl bg-slate-800 p-1" data-fiado-orden>
              {ORDENES.map((o) => (
                <button
                  key={o.valor}
                  type="button"
                  aria-pressed={orden === o.valor}
                  onClick={() => setOrden(o.valor)}
                  data-sin-icono="pestana"
                  className={`flex-1 rounded-lg px-3 py-2 text-sm transition ${orden === o.valor ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'}`}
                >
                  {t(o.nombre)}
                </button>
              ))}
            </div>
          </div>

          {visibles.length === 0 ? (
            <Vacio mensaje={t('porCobrar.nadieAsi')} icono="buscar" />
          ) : (
            <ul className="flex flex-col gap-2" data-fiado-lista>
              {visibles.map((d) => {
                const pasado = pasoSuLimite(d);
                return (
                  <li key={d.clienteId} className="tarjeta" data-deudor={d.clienteId}>
                    {/* The name and what they owe on a line; under it, the whole width, how old, the last payment, the limit. */}
                    <Link to="/clientes/$clienteId" params={{ clienteId: d.clienteId }} className="tarjeta-abrir block" data-sin-icono="fila">
                      <div className="flex items-baseline justify-between gap-3">
                        <p className="min-w-0 font-medium wrap-break-word">
                          {d.nombre}
                          {!d.activo && <span className="ml-2 text-xs font-normal text-slate-500">{t('porCobrar.inactivo')}</span>}
                        </p>
                        <span className="tabular shrink-0 font-medium text-amber-300">{dinero(d.saldo)}</span>
                      </div>
                      <p className={`mt-0.5 text-xs ${d.dias > 90 ? 'text-rose-300' : 'text-slate-500'}`} data-deudor-dias>
                        {d.dias === 0 ? t('porCobrar.deHoy') : t('analisis.haceDias', { n: d.dias })}
                      </p>
                      <p className="text-xs text-slate-500" data-deudor-abono>
                        {d.ultimoAbono ? t('porCobrar.ultimoAbono', { fecha: fechaCorta(d.ultimoAbono) }) : t('porCobrar.nuncaAbono')}
                      </p>
                      {d.limite !== null && (
                        <p className={`text-xs ${pasado ? 'text-rose-300' : 'text-slate-500'}`} data-deudor-limite={pasado ? 'pasado' : 'dentro'}>
                          {t(pasado ? 'porCobrar.pasoLimite' : 'porCobrar.limite', { monto: dinero(d.limite) })}
                        </p>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Pagina>
  );
}
